'use client';

import {
  ActionIcon,
  Container,
  Drawer,
  Group,
  Image,
  Skeleton,
  Stack,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronLeft, IconMenu2 } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { authAtom, loginModalAtom } from '@website/atoms';
import { CategoryButton } from '@website/components/category-button';
import { SearchInput } from '@website/components/search-input';
import { api, handleTreatyResponse } from '@website/utils/api';
import { useAtom, useAtomValue } from 'jotai/react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { CartButton, UserButton } from './buttons';
import cls from './styles.module.css';

export function Header({ initialSearch }: { initialSearch?: string }) {
  const router = useRouter();
  const t = useTranslations();
  const auth = useAtomValue(authAtom);
  const [, setModalOpened] = useAtom(loginModalAtom);
  const [opened, { open, close }] = useDisclosure(false);
  const [search, setSearch] = useState(initialSearch ?? '');

  const categories = useQuery({
    queryKey: ['/c/categories'],
    queryFn: async () => {
      const res = await api.categories[''].get();
      return handleTreatyResponse(res);
    },
  });

  const profile = useQuery({
    queryKey: ['profile'],
    enabled: !!auth.token,
    queryFn: async () => {
      const res = await api.accounts.profile.get({
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      return handleTreatyResponse(res);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      router.push(`/products?search=${encodeURIComponent(search)}`);
    }
  };

  return (
    <div className={cls.header}>
      <Drawer opened={opened} onClose={close} padding={0}>
        <Stack gap={0}>
          {categories.isLoading
            ? Array.from({ length: 10 }).map((_, index) => (
                <Skeleton key={index.toString()} height={30} width={100} />
              ))
            : categories.data?.map((category) => (
                <UnstyledButton
                  key={category.id}
                  variant="default"
                  className={cls.categoriesDrawerButton}
                  onClick={() => {
                    router.push(`/products?categoryId=${category.id}`);
                    close();
                  }}
                >
                  <Group justify="space-between">
                    {category.name}

                    <IconChevronLeft size={16} color="var(--mantine-color-gray-6)" />
                  </Group>
                </UnstyledButton>
              ))}
        </Stack>
      </Drawer>

      <Container p="md">
        <Stack gap="xs">
          <Group justify="space-between">
            <Link href="/">
              <Image w={200} src="/logo.png" />
            </Link>

            <form onSubmit={handleSearch}>
              <SearchInput
                value={search}
                variant="filled"
                onChange={setSearch}
                placeholder={t('searchProducts')}
              />
            </form>

            <Group gap="sm" justify="end" className={cls.actionIcons}>
              <CartButton
                authed={auth.status === 'Authenticated'}
                cartItems={profile.data?.cartItems ?? 0}
                onModalOpen={() => setModalOpened(true)}
              />

              <UserButton
                authed={auth.status === 'Authenticated'}
                onModalOpen={() => setModalOpened(true)}
              />

              <ActionIcon hiddenFrom="sm" size="xl" variant="default" onClick={open}>
                <IconMenu2 />
              </ActionIcon>
            </Group>
          </Group>

          <Group gap="xs" visibleFrom="sm">
            {categories.isLoading
              ? Array.from({ length: 10 }).map((_, index) => (
                  <Skeleton key={index.toString()} height={30} width={100} />
                ))
              : categories.data?.map((category) => (
                  <CategoryButton
                    key={category.id}
                    id={category.id}
                    name={category.name}
                  />
                ))}
          </Group>
        </Stack>
      </Container>
    </div>
  );
}
